import {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';

import { Box, Typography, LinearProgress, useTheme } from '@mui/material';

import { ConfirmTypes, useToastStore } from '@/store';

export interface AudioPlayerProps {
  src: string;
  onPlaybackChanged: (isPlaying: boolean) => void;
}

export interface AudioPlayerHandle {
  togglePlayback: () => void;
  isPlaying: boolean;
}

export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(
  ({ src, onPlaybackChanged }, ref) => {
    const { updateToast } = useToastStore();

    const audioRef = useRef<HTMLAudioElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isScrubbing, setIsScrubbing] = useState(false);

    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
      const secs = Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0');
      return `${mins}:${secs}`;
    };

    const setPlaybackState = (newPlaybackState: boolean) => {
      setIsPlaying(newPlaybackState);
      onPlaybackChanged(newPlaybackState);
    };

    const togglePlayback = async () => {
      const audio = audioRef.current;
      if (!audio) return;

      if (audio.paused) {
        await audio.play().catch((err) => {
          updateToast({
            open: true,
            message: `Error playing recording: ${err.message}`,
            type: ConfirmTypes.ERROR,
          });
        });
      } else {
        audio.pause();
      }

      setPlaybackState(!audio.paused);
    };

    useImperativeHandle(ref, () => ({
      togglePlayback,
      isPlaying,
    }));

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleTimeUpdate = () => {
        if (!isScrubbing) {
          setCurrentTime(audio.currentTime);
        }
      };

      const handleEnded = () => {
        setPlaybackState(false);
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
      };
    }, [isScrubbing, onPlaybackChanged]);

    // Handle drag seeking
    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!isScrubbing || !progressBarRef.current || !audioRef.current)
          return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.min(
          Math.max((e.clientX - rect.left) / rect.width, 0),
          1,
        );
        const newTime = percent * duration;
        setCurrentTime(newTime);
      };

      const handleMouseUp = () => {
        if (!isScrubbing || !audioRef.current) return;

        audioRef.current.currentTime = currentTime;
        setIsScrubbing(false);
      };

      if (isScrubbing) {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isScrubbing, currentTime, duration]);

    useEffect(() => {
      return () => {
        // When component unmounts, signal that playback is no longer active
        onPlaybackChanged(false);
      };
    }, []);

    const handleSeekStart = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!progressBarRef.current || !audioRef.current) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const percent = Math.min(
        Math.max((e.clientX - rect.left) / rect.width, 0),
        1,
      );
      const newTime = percent * duration;
      setCurrentTime(newTime);
      setIsScrubbing(true);
    };

    const progressPercent = duration ? (currentTime / duration) * 100 : 0;

    return (
      <Box sx={{ width: '100%' }}>
        <audio ref={audioRef} src={src} preload="metadata" />

        <Box
          ref={progressBarRef}
          onMouseDown={handleSeekStart}
          sx={{ mt: 2, cursor: 'pointer', position: 'relative' }}
        >
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: theme.palette.divider,
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.error.main,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            fontFamily: 'monospace',
          }}
        >
          <Typography>{formatTime(currentTime)}</Typography>
          <Typography>{formatTime(duration)}</Typography>
        </Box>
      </Box>
    );
  },
);

AudioPlayer.displayName = 'Custom Audio Player';
